import type { HTMLAttributes, ReactNode } from 'react';
import styles from './Telemetry.module.css';

export function StatGroup({
  layout = 'grid',
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLDListElement> & {
  layout?: 'grid' | 'cluster';
  children: ReactNode;
}): ReactNode {
  return (
    <dl
      className={[
        styles.group,
        layout === 'cluster' ? styles.cluster : styles.grid,
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      {children}
    </dl>
  );
}

export function MetricReadout({
  label,
  value,
  unit,
  quiet = false,
  size = 'normal',
}: {
  label: ReactNode;
  value: ReactNode;
  unit?: ReactNode;
  quiet?: boolean;
  size?: 'normal' | 'large';
}): ReactNode {
  return (
    <div className={styles.readout}>
      <dt className={styles.label}>{label}</dt>
      <dd className={styles.valueLine}>
        <span
          className={[
            styles.value,
            size === 'large' ? styles.valueLarge : '',
            quiet ? styles.valueQuiet : '',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {value}
        </span>
        {unit ? <span className={styles.unit}>{unit}</span> : null}
      </dd>
    </div>
  );
}

type StatusTone = 'neutral' | 'live' | 'planned' | 'warning' | 'danger';

const STATUS_CLASS: Record<StatusTone, string> = {
  neutral: styles.statusNeutral ?? '',
  live: styles.statusLive ?? '',
  planned: styles.statusPlanned ?? '',
  warning: styles.statusWarning ?? '',
  danger: styles.statusDanger ?? '',
};

export function StatusIndicator({
  tone = 'neutral',
  children,
}: {
  tone?: StatusTone;
  children: ReactNode;
}): ReactNode {
  return (
    <span className={[styles.status, STATUS_CLASS[tone]].filter(Boolean).join(' ')}>
      <span className={styles.statusDot} aria-hidden="true" />
      <span>{children}</span>
    </span>
  );
}
